import { useCallback, useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import useFirebase from "@/backend/message/useFirebase";
import { messagePromise } from "@/utils/messagePromise";
import { addToast } from "@heroui/react";

export function useRegisterNotification(
  createNotify: (token: string) => Promise<void>
) {
  const [isGranted, setIsGranted] = useState(false);
  const isNative = Capacitor.isNativePlatform();

  // --- 🔹 WEB: Firebase ---
  const { requestPermission: requestFirebasePermission } =
    useFirebase(createNotify);

  // --- 🔹 MOVIL: Listeners ---
  useEffect(() => {
    if (!isNative) return;

    const registerListeners = async () => {
      await PushNotifications.addListener("registration", (token) => {
        messagePromise(createNotify(token.value), {
          pending: "Agregando a notificaciones",
          success: "Dispositivo registrado correctamente",
          error: "Error al registrar dispositivo",
        });
      });

      await PushNotifications.addListener("registrationError", (err) => {
        addToast({
          title: "Hubo un error",
          description: "No vas a recibir notificiones de la app",
        });
      });

      await PushNotifications.addListener(
        "pushNotificationReceived",
        (notification) => {
          addToast({
            title: notification?.title,
            description: notification?.body,
          });
        }
      );

      await PushNotifications.addListener(
        "pushNotificationActionPerformed",
        (notification) => {
          console.log(
            "👉 Notification action performed",
            notification.actionId,
            notification.inputValue
          );
        }
      );
    };

    registerListeners();

    // opcional: limpiar listeners al desmontar
    return () => {
      PushNotifications.removeAllListeners();
    };
  }, [isNative, createNotify]);

  // --- 🔹 Registro en Capacitor ---
  const registerCapacitorNotifications = useCallback(async () => {
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === "prompt") {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== "granted") {
      console.warn("❌ User denied push permission");
      setIsGranted(false);
      return;
    }

    setIsGranted(true);
    await PushNotifications.register();
  }, []);

  // --- 🔹 API público del hook ---
  const registerNotifications = useCallback(() => {
    if (isNative) {
      // 📱 Móvil
      registerCapacitorNotifications();
    } else {
      // 💻 Web
      requestFirebasePermission();
    }
  }, [registerCapacitorNotifications, requestFirebasePermission]);

  return {
    isGranted,
    isNative,
    registerNotifications,
  };
}
